/**
 * @file AppContext.tsx
 * @description This file contains the AppContext component which provides global state and functionality for the application.
 */

import DialogLoading from '@components/dialog/dialog-loading';
import useGenpar from '@hooks/useGenpar';
import { InformationDialog } from '@hooks/useInformationDialog';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  setExpiredSession,
  setIsMaintenance,
  setSystemDialogs,
} from '@store/slices/api.slice';
import { IGenpar, IGenparAPI } from '@store/slices/genpar.slice';
import { saveUserThunk, UserLogin } from '@store/slices/user.slice';
import { IPOSMenuImage, posLocalForage } from '@utils/localforage';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useEffect, useState } from 'react';
import DialogMessage from '../components/dialog/dialog-message';
import DialogSecCode, {
  VerifySecCodeInterface,
} from '../components/dialog/dialog-sec-code';
import DialogSessionExpired from '../components/dialog/dialog-session-expired';
import { isDataString } from '../utils/format';
import { hashName } from '../utils/security';
import { selectMaintenanceMode } from '@store/selector/api.selector';

// #region Interface & Type

/**
 * @interface Error500
 * @description Interface for 500 error responses
 */
export interface Error500 {
  status: number;
  message: string;
  data: {
    error_message: string;
    level: number;
    url: string;
    method: string;
    errors: {
      error: {
        remarks:
          | string
          | {
              expiredAt: string;
              message: string;
              name: string;
            };
      };
    }[];
  };
}

/**
 * @interface AlertState
 * @description Interface for alert state
 */
export interface AlertState {
  open?: boolean | null | undefined;
  duration?: number | null | undefined;
  severity?:
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'default'
    | null
    | undefined;
  message?: any;
  errorLevel?: 1 | 2 | 0 | 500 | null | undefined;
  errorRes?: Error500;
  onClose?: () => void;
}

export interface IPOSForage {
  subscribe: (callback: () => Promise<void>) => () => void;
  getSnapshot: (stationID: string) => Promise<IPOSMenuImage[]>;
  setItem: (
    stationID: string,
    value: IPOSMenuImage[] | IPOSMenuImage,
  ) => Promise<void>;
  clearStore: (stationID: string) => Promise<void>;
}

/**
 * @type AppContextType
 * @type AppContextType
 * @description Type for alert context
 */
export type AppContextType = {
  // TOAST ALERT
  toast: (data: AlertState) => Promise<void>;

  // SEC CODE DIALOG
  isOpenSecCodeDialog: boolean;
  secCodePriv: string;
  openSecCodeDialog: (
    confirmCallback: (params?: VerifySecCodeInterface) => Promise<void>,
    priv: string,
    cancelCallback?: () => void,
    hideNumpad?: boolean,
  ) => Promise<void>;
  closeSecCodeDialog: () => void;
  onConfirmSecCode: ((params: VerifySecCodeInterface) => Promise<void>) | null;

  // LOADING
  startLoading: () => void;
  stopLoading: () => void;
  startLoadingStatus: (status: string) => void;
  stopLoadingStatus: () => void;
  isSecuCodeCancel: boolean;
  cancelSecCodeDialog: () => void;
  loadingDelay: (ms?: number) => Promise<void>;
  genpar: {
    genparData: IGenpar[];
    clearGenparState: () => void;
    getGenparValue: (key: string, subModuleCode?: string) => string;
    refetchGenpar: () => Promise<{
      data?: IGenparAPI;
      error?: SerializedError | FetchBaseQueryError;
    }>;
  };
  setCurrentDialogIndex: React.Dispatch<React.SetStateAction<number>>;
  setStoreUser: React.Dispatch<React.SetStateAction<UserLogin | undefined>>;
  posForage: IPOSForage;
};

/**
 * @interface Props
 * @description Interface for AppContext props
 */
interface Props {
  children: React.ReactNode;
}
// #endregion

// #region Context
export const WebAppContext = React.createContext<AppContextType | null>(null);

const MemoizedWebAppContextProvider = React.memo(WebAppContext.Provider);
// #endregion

// #region Snackbar
/**
 * @function CustomNotificationContent
 * @description Custom component for notification content
 * @param {Object} props - Component props
 * @param {string} props.message - Message to display
 * @returns {JSX.Element} Rendered component
 */
export const CustomNotificationContent = ({ message }: { message: string }) => (
  <div
    style={{
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      display: 'block',
      maxWidth: '100%',
      width: 'auto',
    }}
    dangerouslySetInnerHTML={{ __html: message }}
  />
);

/**
 * @function getVariant
 * @description Determine the variant of the notification based on error level and severity
 * @param {number | undefined} errorLevel - Error level
 * @param {'success' | 'warning' | 'error' | 'info' | 'default' | undefined} severity - Severity of the notification
 * @returns {string} Variant of the notification
 */
const getVariant = (
  errorLevel: number | undefined,
  severity: 'success' | 'warning' | 'error' | 'info' | 'default' | undefined,
) => {
  if (errorLevel === 1) return 'error';
  if (errorLevel === 2) return 'warning';
  return severity !== null ? severity : 'default';
};

/**
 * @function getAutoHideDuration
 * @description Determine the auto-hide duration of the notification based on duration and severity
 * @param {number | undefined} duration - Custom duration
 * @param {'success' | 'warning' | 'error' | 'info' | 'default' | undefined} severity - Severity of the notification
 * @returns {number} Auto-hide duration in milliseconds
 */
const getAutoHideDuration = (
  duration: number | undefined,
  severity: 'success' | 'warning' | 'error' | 'info' | 'default' | undefined,
): number => {
  if (duration !== undefined) return duration;
  switch (severity) {
    case 'error':
      return 15000;
    case 'warning':
      return 7000;
    default:
      return 3000;
  }
};

/**
 * @function sanitizeMessage
 * @description Sanitize and clean the message text
 * @param {unknown} messageText - Message to sanitize
 * @returns {string} Sanitized message
 */
export const sanitizeMessage = (messageText: unknown) => {
  const text = String(messageText);
  const cleanedText = text.replace(/Error:\s*/g, '');

  return DOMPurify.sanitize(cleanedText, {
    ALLOWED_TAGS: [
      'b',
      'strong',
      'i',
      'em',
      'u',
      'p',
      'br',
      'ul',
      'ol',
      'li',
      'span',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'a',
    ],
    ALLOWED_ATTR: ['style', 'href', 'target', 'rel'],
  });
};

const displayedMessages = new Set();
const SEC_CODE_GRACE_PERIOD_MS = 10 * 60 * 1000;
const SEC_CODE_GRACE_STORAGE_KEY = 'secCodeGraceExpiresAt';
const SEC_CODE_GRACE_STORAGE_SCOPE = 'accounting-transactions';
const ACCOUNTING_TRANSACTION_SEC_CODE_GRACE_PRIVS = new Set([
  'post_sales',
  'post_sales_reversal',
  'post_sales_rcpt',
  'post_disbursements',
  'post_purchases',
  'post_bills_reversal',
  'post_vendor_credit',
  'post_debit_note',
  'post_adjustments',
]);

type SecCodeGraceStorage = {
  expiresAtByPriv: Record<string, number>;
  scope: string;
};
// #endregion

/**
 * @function AppContext
 * @description Main AppContext component that provides global state and functionality
 * @param {Props} props - Component props
 * @returns {JSX.Element} Rendered component
 */

/**
 * @function handleCloseTab
 * @description Handle tab close event
 * @param {BeforeUnloadEvent} e - BeforeUnload event object
 */
export const handleCloseTab = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = '';

  // Display a confirmation dialog with 'Yes' and 'No' buttons
  var confirmationMessage =
    'Are you sure you want to leave? Unsaved data will be lost.';
  e.returnValue = confirmationMessage;

  // Custom dialog box with 'Yes' and 'No' buttons
  if (confirm(confirmationMessage)) {
    // User clicked 'Yes' button
    // Additional logic can be added here
  } else {
    // User clicked 'No' button
    // Additional logic can be added here
  }
};

const AppContext: React.FC<Props> = ({ children }) => {
  // #region SEC CODE DIALOG
  const [isOpenSecCodeDialog, setIsOpenSecCodeDialog] = useState(false);
  const [secCodePriv, setSecCodePriv] = useState('');
  const [onConfirmSecCode, setOnConfirmSecCode] = useState<
    ((res: VerifySecCodeInterface) => Promise<void>) | null
  >(null);
  const [onCancelSecCode, setOnCancelSecCode] = useState<() => void>();

  const [isSecuCodeCancel, setSecuCodeCancel] = useState(false);
  const [hideNumpadState, setHideNumpad] = useState<boolean>();
  const secCodeGraceRef = React.useRef<
    Record<
      string,
      {
        expiresAt: number;
        response: VerifySecCodeInterface;
      }
    >
  >({});

  const getStoredSecCodeGrace = () => {
    if (typeof window === 'undefined') {
      return { expiresAtByPriv: {}, isAvailable: false };
    }

    try {
      const storedGraceValue = window.sessionStorage.getItem(
        SEC_CODE_GRACE_STORAGE_KEY,
      );
      if (!storedGraceValue) {
        return { expiresAtByPriv: {}, isAvailable: true };
      }

      let storedGrace: {
        expiresAtByPriv?: unknown;
        scope?: unknown;
      };

      try {
        storedGrace = JSON.parse(storedGraceValue);
      } catch {
        window.sessionStorage.removeItem(SEC_CODE_GRACE_STORAGE_KEY);
        return { expiresAtByPriv: {}, isAvailable: true };
      }

      if (
        storedGrace?.scope !== SEC_CODE_GRACE_STORAGE_SCOPE ||
        !storedGrace?.expiresAtByPriv ||
        typeof storedGrace.expiresAtByPriv !== 'object' ||
        Array.isArray(storedGrace.expiresAtByPriv)
      ) {
        window.sessionStorage.removeItem(SEC_CODE_GRACE_STORAGE_KEY);
        return { expiresAtByPriv: {}, isAvailable: true };
      }

      const expiresAtByPriv = Object.entries(
        storedGrace.expiresAtByPriv as Record<string, unknown>,
      ).reduce<Record<string, number>>((validEntries, [priv, expiresAt]) => {
        const numericExpiresAt = Number(expiresAt);

        if (Number.isFinite(numericExpiresAt)) {
          validEntries[priv] = numericExpiresAt;
        }

        return validEntries;
      }, {});

      return { expiresAtByPriv, isAvailable: true };
    } catch {
      return { expiresAtByPriv: {}, isAvailable: false };
    }
  };

  const saveStoredSecCodeGrace = (expiresAtByPriv: Record<string, number>) => {
    try {
      const storagePayload: SecCodeGraceStorage = {
        expiresAtByPriv,
        scope: SEC_CODE_GRACE_STORAGE_SCOPE,
      };

      window.sessionStorage.setItem(
        SEC_CODE_GRACE_STORAGE_KEY,
        JSON.stringify(storagePayload),
      );
    } catch {
      // Ignore storage failures. The in-memory cache still covers this tab.
    }
  };

  const clearSecCodeGrace = (priv?: string) => {
    if (priv) {
      delete secCodeGraceRef.current[priv];
    } else {
      secCodeGraceRef.current = {};
    }

    try {
      if (!priv) {
        window.sessionStorage.removeItem(SEC_CODE_GRACE_STORAGE_KEY);
        return;
      }

      const storedGrace = getStoredSecCodeGrace();
      const nextExpiresAtByPriv = { ...storedGrace.expiresAtByPriv };
      delete nextExpiresAtByPriv[priv];

      if (Object.keys(nextExpiresAtByPriv).length) {
        saveStoredSecCodeGrace(nextExpiresAtByPriv);
      } else {
        window.sessionStorage.removeItem(SEC_CODE_GRACE_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures. The in-memory cache is still cleared.
    }
  };

  const getCachedSecCodeResponse = (
    res?: VerifySecCodeInterface,
  ): VerifySecCodeInterface => ({
    status: res?.status ?? 200,
    data: {
      ...res?.data,
      isValid: true,
      message: res?.data?.message ?? 'Security code verified',
    },
    message: res?.message ?? 'Security code verified',
    buildVersion: res?.buildVersion ?? '',
    securityCode: '',
  });

  const getActiveSecCodeGrace = (priv: string) => {
    const cachedGrace = secCodeGraceRef.current[priv];
    const storedGrace = getStoredSecCodeGrace();
    const storedExpiresAt = storedGrace.expiresAtByPriv[priv];

    if (storedGrace.isAvailable && !storedExpiresAt && cachedGrace) {
      delete secCodeGraceRef.current[priv];
      return null;
    }

    const expiresAt = storedExpiresAt ?? cachedGrace?.expiresAt;

    if (!expiresAt) return null;

    if (Date.now() > expiresAt) {
      clearSecCodeGrace(priv);
      return null;
    }

    if (cachedGrace) {
      return cachedGrace.response;
    }

    const response = getCachedSecCodeResponse();
    secCodeGraceRef.current[priv] = {
      expiresAt,
      response,
    };

    return response;
  };

  const rememberSecCodeGrace = (priv: string, res: VerifySecCodeInterface) => {
    const expiresAt = Date.now() + SEC_CODE_GRACE_PERIOD_MS;

    secCodeGraceRef.current[priv] = {
      expiresAt,
      response: getCachedSecCodeResponse(res),
    };

    saveStoredSecCodeGrace({
      ...getStoredSecCodeGrace().expiresAtByPriv,
      [priv]: expiresAt,
    });
  };

  const canUseSecCodeGrace = (priv?: string) =>
    ACCOUNTING_TRANSACTION_SEC_CODE_GRACE_PRIVS.has(priv ?? '');

  /**
   * @function openSecCodeDialog
   * @description Open the security code dialog
   * @param {() => Promise<void>} confirmCallback - Callback function to execute on confirmation
   * @param {string} priv - Privilege string
   */
  const openSecCodeDialog = (
    confirmCallback: (res: VerifySecCodeInterface) => Promise<void>,
    priv: string,
    cancelCallback?: () => void,
    hideNumpad?: boolean,
  ) => {
    const canUseGrace = canUseSecCodeGrace(priv);
    const cachedSecCodeResponse = canUseGrace
      ? getActiveSecCodeGrace(priv)
      : null;

    if (cachedSecCodeResponse) {
      return confirmCallback(cachedSecCodeResponse);
    }

    return new Promise<void>((resolve, reject) => {
      setIsOpenSecCodeDialog(true);
      setSecCodePriv(priv);
      setSecuCodeCancel(false);
      setHideNumpad(hideNumpad);

      setOnConfirmSecCode(() => async (res: VerifySecCodeInterface) => {
        try {
          if (canUseGrace && res?.data?.isValid) {
            rememberSecCodeGrace(priv, res);
          }
          await confirmCallback(res);
          setIsOpenSecCodeDialog(false);
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          setHideNumpad(false);
        }
      });

      if (cancelCallback) {
        setOnCancelSecCode(() => () => {
          cancelCallback();
          setIsOpenSecCodeDialog(false);
          setHideNumpad(false);
          reject(new Error('Security Code Cancelled'));
        });
      } else {
        setOnCancelSecCode(() => () => {
          setIsOpenSecCodeDialog(false);
          setHideNumpad(false);
          reject(new Error('Security Code Cancelled'));
        });
      }
    });
  };

  /**
   * @function closeSecCodeDialog
   * @description Close the security code dialog
   */
  const closeSecCodeDialog = () => {
    setIsOpenSecCodeDialog(false);
    setOnConfirmSecCode(null);
    setSecCodePriv('');
    setSecuCodeCancel(false);

    setTimeout(() => {
      setOnCancelSecCode(undefined);
    }, 100);
  };

  /**
   * @function cancelSecCodeDialog
   * @description Cancel the security code dialog
   */
  const cancelSecCodeDialog = () => {
    setIsOpenSecCodeDialog(false);
    setSecuCodeCancel(true);
    setOnConfirmSecCode(null);
    setSecCodePriv('');

    setTimeout(() => {
      setOnCancelSecCode(undefined);
    }, 100);
  };

  // #endregion

  // #region ALERT
  const { enqueueSnackbar } = useSnackbar();
  const [uiError, setUiError] = useState<Error500 | null | undefined>();

  /**
   * @function toast
   * @description Display a toast notification
   * @param {AlertState} data - Alert state data
   */
  const toast = async (data: AlertState) => {
    const { duration, message, severity, errorLevel } = data;

    const messageText = isDataString(message)
      ? message
      : message?.errMsg?.toString();

    const errorLevelToUse = isDataString(message)
      ? errorLevel
      : message?.errLevel;

    if (message?.errLevel === 500) {
      return setUiError(message?.errMsg);
    }

    const severityToUse = severity ?? undefined;
    const durationToUse =
      errorLevelToUse == 1
        ? 15000
        : errorLevelToUse == 2
          ? 7000
          : (duration ?? undefined);

    if (displayedMessages.has(messageText)) {
      return;
    }

    displayedMessages.add(messageText);

    enqueueSnackbar(
      <CustomNotificationContent message={sanitizeMessage(messageText)} />,
      {
        variant: getVariant(errorLevelToUse, severityToUse),
        autoHideDuration: getAutoHideDuration(durationToUse, severityToUse),
        onClose: (_, reason) => {
          displayedMessages.delete(messageText);
          if (data?.onClose || (reason === 'timeout' && data?.onClose)) {
            data.onClose();
          }
        },
      },
    );
  };
  // #endregion

  // #region MAINTENANCE DIALOG
  const user = useAppSelector((state) => state.user);
  const isMaintenance = useAppSelector(selectMaintenanceMode);
  const systemDialogs = useAppSelector((state) => state.apiSlice.systemDialogs);
  const dispatch = useAppDispatch();

  const [currentDialogIndex, setCurrentDialogIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [storeUser, setStoreUser] = useState<UserLogin>();

  const handleDialogClose = async () => {
    try {
      setShowDialog(false);
      setTimeout(async () => {
        if (currentDialogIndex + 1 < systemDialogs.length) {
          setCurrentDialogIndex(currentDialogIndex + 1);
        } else {
          dispatch(setSystemDialogs([]));
          setCurrentDialogIndex(0);

          const currentDialog =
            systemDialogs[
              systemDialogs.length > 1 ? currentDialogIndex - 1 : 0
            ];

          if (storeUser) {
            startLoading();
            await dispatch(saveUserThunk(storeUser))
              .unwrap()
              .then()
              .finally(() => {
                stopLoading();
                setStoreUser(undefined);
              });
          }

          if (isMaintenance && user?.info?.token) {
            dispatch(setIsMaintenance(false));
            dispatch(setExpiredSession(true));
          }

          if (currentDialog?.cb) {
            currentDialog?.hasLoading && startLoading();
            await currentDialog.cb();
          }
        }
      }, 500);
    } catch (error) {
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (systemDialogs.length <= 0) return;

    const currentDialog = systemDialogs[currentDialogIndex];

    if (!currentDialog) return;

    if (currentDialog.dialog_type === 'Notification Message') {
      toast({
        severity: currentDialog?.severity ?? 'info',
        message: currentDialog.message,
      });
      handleDialogClose();
    } else {
      setShowDialog(true);
    }
  }, [systemDialogs, currentDialogIndex]);

  // #endregion

  // #region GLOBAL LOADING UI
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>();

  const startLoadingStatus = (status: string) => setLoadingStatus(status);
  const stopLoadingStatus = () => setLoadingStatus(undefined);

  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);

  const loadingDelay = async (ms?: number) => {
    startLoading();
    await new Promise((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, ms || 100);
    });
    stopLoading();
  };
  // #endregion

  // #region EVENT LISTENER
  /**
   * @function handleOnline
   * @description Handle online event
   */
  const handleOnline = () => {
    toast({ message: 'Back Online', duration: 10000 });
  };

  /**
   * @function handleOffline
   * @description Handle offline event
   */
  const handleOffline = () => {
    toast({
      message: 'No Internet Connection',
      severity: 'error',
      errorLevel: 0,
      duration: 10000,
    });
  };

  /**
   * @function handleStorageChange
   * @description Handle storage change event
   * @param {StorageEvent} event - Storage event object
   */
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === hashName('expiredToken') && event.newValue == '1') {
      dispatch(setExpiredSession(true));
      localStorage.setItem(hashName('expiredToken'), '0');
    }
  };

  useEffect(() => {
    localStorage.setItem(hashName('expiredToken'), '0');

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', handleCloseTab);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeunload', handleCloseTab);

      setUiError(null);
    };
  }, []);
  // #endregion

  // #region GENERAL PARAMETER
  const genpar = useGenpar({
    startLoading,
    stopLoading,
  });
  const { genparData } = genpar;
  // #endregion

  // #region LOCAL FORAGE
  const posForage = posLocalForage();
  //  #endregion

  const memoizedValues = React.useMemo(
    () => ({
      toast,

      // SEC CODE
      isOpenSecCodeDialog,
      secCodePriv,
      openSecCodeDialog,
      closeSecCodeDialog,
      onConfirmSecCode,

      // LOADING
      startLoading,
      stopLoading,
      startLoadingStatus,
      stopLoadingStatus,
      isSecuCodeCancel,
      cancelSecCodeDialog,
      loadingDelay,

      // GENPAR
      genpar,

      // MAINTENANCE DIALOG
      setCurrentDialogIndex,
      setStoreUser,

      // LOCAL FORAGE
      posForage,
    }),
    [genparData],
  );

  return (
    <>
      <MemoizedWebAppContextProvider value={memoizedValues}>
        {children}
      </MemoizedWebAppContextProvider>

      {/* SESSION EXPIRED DIALOG */}
      <DialogSessionExpired {...{ toast }} />

      {/* SECURITY CODE DIALOG */}
      <DialogSecCode
        {...{
          isOpenSecCodeDialog,
          closeSecCodeDialog,
          onConfirmSecCode,
          secCodePriv,
          cancelSecCodeDialog,
          toast,
          onCancelSecCode,
          hideNumpad: hideNumpadState,
        }}
      />

      {/* LOADING */}
      <DialogLoading {...{ isLoading }} />
      <DialogLoading
        {...{
          isLoading: !!loadingStatus,
          desc: loadingStatus,
        }}
      />

      {/* API ERROR UI */}
      <DialogMessage
        isOpen={uiError ? true : false}
        error={uiError}
        onClose={() => setUiError(null)}
      />

      {/* MAINTENANCE DIALOG */}
      <InformationDialog
        open={showDialog}
        onClose={handleDialogClose}
        desc={
          isMaintenance
            ? 'We&apos;re currently making some updates to improve your experience.\nPlease check back soon, and thank you for your patience!'
            : systemDialogs[currentDialogIndex]?.message
              ? sanitizeMessage(systemDialogs[currentDialogIndex].message)
              : 'No Message Found'
        }
        title={isMaintenance ? 'Notice (On Maintenance)' : 'Notice'}
        btnProps={{ variant: 'contained' }}
      />
    </>
  );
};

export default AppContext;
